<template>
  <div
    v-editable="blok"
    class="IconListBlock layout-container"
    :class="[desktopWidth('md'), blockPaddingTop('md'), blockPaddingBottom('md')]"
  >
    <div
      v-if="blok.headingTitle || blok.headingButtonText"
      class="flex flex-row flex-wrap justify-between items-center gap-8 mb-16"
      :class="{'flex-col text-center' : blok.headingAlign === 'center'}"
    >
      <div class="type-headline-lg desk:type-headline-2xl"> {{ blok.headingTitle }}</div>

      <Nuxt-link
        :to="$sbLinkFix(blok.headingButtonLink)"
        :target="$sbLinkTarget(blok.headingButtonLink)"
      >
        <div
          v-if="blok.headingButtonText"
          class="btn btn--primary"
          :class="{
            'btn--secondary': blok.headingButtonStyle === 'secondary',
            'btn--ghost': blok.headingButtonStyle === 'ghost',
            'btn--text': blok.headingButtonStyle === 'plain',
          }"
        >
          {{ blok.headingButtonText }}
        </div>
      </Nuxt-link>
    </div>
    <div v-if="(blok.displaySlider) && isMobile">
      <client-only>
        <Swiper
          class="swiper-cover !overflow-visible !pb-8"
          :modules="[SwiperPagination, SwiperA11y]"
          :space-between="24"
          :pagination="{
            clickable: true,
            dynamicBullets: true,
          }"
          :loop="true"
          :slides-per-view="1"
          :no-swiping="true"
        >
          <SwiperSlide
            v-for="iconItem in blok.itemList"
            :key="iconItem._uid"
            class="!flex last:mb-0 w-full"
            :class="{
              'flex-row items-center mb-24': blok.displaySlimLayout,
              'flex-col items-center mb-32': !blok.displaySlimLayout
            }"
          >
            <div
              :class="{
                'h-40 w-40 mb-12': !blok.displaySlimLayout,
                'h-40 w-40 mr-12': blok.displaySlimLayout
              }"
            >
              <nuxt-img
                v-if="iconItem.icon && iconItem.icon.filename"
                preset="standard"
                loading="lazy"
                sizes="24px desk:40px"
                class="h-full w-full object-fit"
                :src="iconItem.icon.filename"
                :alt="iconItem.icon.alt"
              />
            </div>
            <div
              :class="{
                'text-center': !blok.displaySlimLayout,
              }"
            >
              <div
                class="type-headline-sm"
                :class="{
                  'mb-4': !blok.displaySlimLayout,
                  '-mt-2': blok.displaySlimLayout,
                }"
              >
                {{ iconItem.title }}
              </div>
              <div class="type-sm text-text-subtle">
                {{ iconItem.text }}
              </div>
            </div>
          </SwiperSlide>
        </Swiper>
      </client-only>
    </div>
    <div v-else class="flex flex-row flex-wrap desk:flex-nowrap justify-center gap-32 desk:gap-64">
      <div
        v-for="(iconItem, index) in blok.itemList"
        :key="iconItem._uid"
        class="flex last:mb-0"
        :class="{
          'desk:mt-24': blok.displaySlimLayout && index >= (blok.displaySlimLayout ? 4 : 4),
          'flex-row items-center w-full icon-item-slim': blok.displaySlimLayout,
          'flex-col items-center icon-item-regular': !blok.displaySlimLayout
        }"
      >
        <div
          class="shrink-0"
          :class="{
            'h-40 w-40': !blok.displaySlimLayout,
            'h-40 w-40 mr-16': blok.displaySlimLayout
          }"
        >
          <nuxt-img
            v-if="iconItem.icon && iconItem.icon.filename"
            preset="standard"
            loading="lazy"
            sizes="24px desk:40px"
            class="h-full w-full object-fit"
            :src="iconItem.icon.filename"
            :alt="iconItem.icon.alt"
          />
        </div>
        <div
          :class="{
            'text-center': !blok.displaySlimLayout,
          }"
        >
          <div
            class="type-headline-sm"
            :class="{
              '-mt-0 mb-0': blok.displaySlimLayout,
              'mt-16 mb-4': !blok.displaySlimLayout,
            }"
          >
            {{ iconItem.title }}
          </div>
          <div class="type-sm text-text-subtle">
            {{ iconItem.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { StoryBlokAsset, StoryBlokLink, StoryBlokPropBase } from '~/constants/types/storyblok';
import useVisualAppearancePlugin from '~/composeables/useVisualAppearancePlugin';
import { useUiStore } from '~/store/ui';
import { storeToRefs } from 'pinia';

const uiStore = useUiStore();
const { isMobile } = storeToRefs(uiStore);
export type IconListItemType = StoryBlokPropBase & {
  icon: StoryBlokAsset;
  text?: string;
  title: string;
}

type Props = StoryBlokPropBase & {
  itemList: IconListItemType[];
  displaySlider: boolean;
  displaySlimLayout: boolean;
  // Heading fields
  headingTitle: string;
  headingButtonText: string;
  headingButtonLink: StoryBlokLink;
  headingButtonStyle: 'primary' | 'secondary' | 'ghost' | 'plain';
  headingAlign: 'left' | 'center';
}

const props = defineProps<{
  blok: Props,
}>();

const { desktopWidth, blockPaddingTop, blockPaddingBottom } = useVisualAppearancePlugin(props);
</script>
